import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { formatNumber, getISO2ByISO3, getNameByISO3 } from "../../../../libs/misc";
import useFetch from "../../../../libs/useFetch";
import DataTable from "../../../../../ui-kit/DataTable/DataTable";
import { Link } from "react-router-dom";
import { Form } from "react-bootstrap";

interface DataRow {
  rep: string;
  tw_tariff: number;
  sa_tariff: number;
  import_from_khm: number;
  total: number
}

// Re-introduce columnHelper from @tanstack/react-table for defining columns
const columnHelper = createColumnHelper<DataRow>();

const TradePotentialByPartnerTable: FC = () => {
  const [dataTable, setDataTable] = useState<DataRow[]>([]);
  const [rawData, setRawData] = useState<any[]>([]);
  const { fetchCsvData } = useFetch();
  const [isLoading, setIsLoading] = useState(true);
  const [includeZero, setIncludeZero] = useState(false);
  
  //Get Data from CSV File or API
  useEffect(() => {
    setIsLoading(true);
    fetchCsvData('data/trade-potential/KHM.csv', setRawData)
  }, []);

  //Transform raw data into the format expected by DataTable
  useEffect(()=>{
    if (rawData.length === 0) return;

    const filteredData = rawData.filter((x=>x.par==='KHM' && x.tariff !== null))
    const grouped = {};
    filteredData.forEach(item => {
      const key = item.rep;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(item);
    });

    const summarised:DataRow[] = Object.entries(grouped).map(([rep, items]) => {
      const itemsArray = items as Array<any>;
      const totalVal = itemsArray.reduce((sum, d) => sum + d?.imports, 0);
      const weightedTariff = itemsArray.reduce((sum, d) => sum + d.tariff * d.imports, 0) / totalVal;
      const averageTariff = itemsArray.reduce((sum, d) => sum + d.tariff, 0) / itemsArray.length;
      const totalChange = itemsArray.reduce((sum, d) => sum + d.tot, 0)
      const importTotal = totalVal;

      return {
        rep,
        tw_tariff: weightedTariff,
        sa_tariff: averageTariff,
        import_from_khm: importTotal,
        total: totalChange
      };
    });
    setDataTable(summarised);
    setIsLoading(false);
  }, [rawData]);

  // 3. Define Columns for the DataTable using columnHelper
  const columns: ColumnDef<DataRow, any>[] = useMemo(() => [
    columnHelper.accessor('rep', {
      header: 'Economy',
      cell: info => (
      <Link to={`/third-party-impacts?rep=KHM&par=${info.getValue()}`}>
        <span className={`fi fi-${getISO2ByISO3(info.getValue())?.toLowerCase() || ''} me-2`}></span> 
        {getNameByISO3(info.getValue())}
      </Link>
      ),
      meta: {
        headerClass: 'text-center',
        cellClass: 'text-left font-semibold text-dark',
      },
      enableSorting: true,
      enableGlobalFilter: true,
    }),
    columnHelper.accessor('tw_tariff', {
      header: 'Trade Weight Tariff',
      cell: info => `${Math.round(info.getValue())}%`,
      meta: {
        cellClass: 'text-end',
        headerClass: 'font-bold'
      },
      enableSorting: true,
      enableGlobalFilter: true,
    }),
    columnHelper.accessor('sa_tariff', {
      header: 'Average Tariff',
      cell: info => `${Math.round(info.getValue())}%`,
      meta: {
        cellClass: 'text-end',
        headerClass: "font-bold"
      },
      enableSorting: true,
      enableGlobalFilter: false,
    }),
    columnHelper.accessor('import_from_khm', {
      header: 'Import from KHM',
      cell: info => formatNumber(Math.round(info.getValue())),
      meta: {
        cellClass: 'text-end',
        headerClass: "font-bold"
      },
      enableSorting: true,
      enableGlobalFilter: false,
    }),
    columnHelper.accessor('total', {
      header: 'Total Potential Change',
      cell: info => {
        const value = Math.round(info.getValue());
        if(value>0){
          return (<span className="text-success fw-bolder">+{formatNumber(value)}</span>)
        }else if(value<0){
          return (<span className="text-danger fw-bolder">-{formatNumber(value)}</span>)
        }
        return (<span className="fw-bolder">{formatNumber(value)}</span>)
      },
      meta: {
        headerClass: 'font-bold',
        cellClass: 'text-end',
      },
      filterFn: (row, columnId, filterValue) => {
        const value = row.getValue<number>(columnId);
        return filterValue === true // includeZero
          ? true                     // show everything
          : value !== 0;             // exclude zero values      
      },
      enableSorting: true,
      enableGlobalFilter: false,
    }),
  ], [includeZero]); // Columns don't depend on state, so empty dependency array is fine here

  
  return (
    <>
      <Form.Check
        type="switch"
        id="partner-table-zero-toggle"
        label="Include total potential change of zero"
        checked={includeZero}
        onChange={(e) => setIncludeZero(e.target.checked)}
        className="form-switch form-switch-sm mb-1 mt-10"
        style={{ minHeight: "1.5rem" }}
      />
      <DataTable<DataRow>
        data={dataTable}
        columns={columns}
        isLoading={isLoading}
        enablePagination={true}
        enableSorting={true}
        enableGlobalFilter={true}
        globalFilterPlaceholder=""
        defaultPageSize={20}
        pageSizes={[5, 10, 20, 50, 100]}
        className="my-custom-table-wrapper"
        initialSortBy={{ id: 'import_from_khm', desc: true }} 
        initialColumnFilters={[
          {
            id: 'total',
            value: includeZero 
          }
        ]} 
      />
    </>
  );
};

export default TradePotentialByPartnerTable;
